import { PATH_WIDTH } from 'constants/map';
import { Position } from 'pages/Map/types';
import { Indicator, Point } from './types';
import { Direction } from 'types';

/**
 * creates an svgpath from given points
 */
export const createPathFromPoints = (points: Position[], indicators: Indicator[]) => {
    const filteredPoints = removeIntermediateStraightPoints(points);
    const path = filteredPoints.reduce((path, point, index, array) => {
        if (index === 0) {
            return (path += `M ${point.x} ${point.y}`);
        }

        if (!array[index + 1]) {
            return (path += ` L ${point.x} ${point.y}`);
        }

        const prevPoint = array[index - 1];
        const nextPoint = array[index + 1];
        return (path += createSmoothCurvedPath(point, nextPoint, prevPoint));
    }, '');

    return { path, indicators };
};

/**
 * This method will returns the smooth curved path between the current and next points based on the previous point
 * Let's say if there is path top to bottom point, curve direction depends on the previous point left / right.
 * - If previous point is on the left, path is coming from the left and takes down turn with curve
 * - If previous point is on the right, path is coming from the right and takes down turn with curve
 *
 * More details at https://github.com/mantacares/planner/blob/main/docs/design/zone-rendering.md#smoothing-curves
 */
export const createSmoothCurvedPath = (point: Point, nextPoint: Point, prevPoint: Point) => {
    const verticalDirection = getPathVerticalDirection(point, nextPoint);
    const horizontalDirection = getPathHorizontalDirection(point, nextPoint);

    if (verticalDirection) {
        const prevPointDirection = getPathHorizontalDirection(point, prevPoint);
        const xCurveFactor = prevPointDirection === Direction.LEFT ? PATH_WIDTH : -PATH_WIDTH;
        const yCurveFactor = verticalDirection === Direction.DOWN ? PATH_WIDTH : -PATH_WIDTH;
        return ` L ${point.x - xCurveFactor} ${point.y} C ${point.x} ${point.y} ${point.x} ${point.y} ${point.x} ${point.y + yCurveFactor}`;
    }

    const prevPointDirection = getPathVerticalDirection(point, prevPoint);
    const yCurveFactor = prevPointDirection === Direction.UP ? PATH_WIDTH : -PATH_WIDTH;
    const xCurveFactor = horizontalDirection === Direction.LEFT ? PATH_WIDTH : -PATH_WIDTH;
    return ` L ${point.x} ${point.y - yCurveFactor} C ${point.x} ${point.y} ${point.x} ${point.y} ${point.x - xCurveFactor} ${point.y}`;
};

export const removeIntermediateStraightPoints = (points: Position[]) => {
    const filteredPoints = points.filter((point, index, array) => {
        if (index === 0 || !array[index + 1]) {
            return true;
        }

        const prevPoint = array[index - 1];
        const nextPoint = array[index + 1];

        return !(
            (point.x === prevPoint.x && nextPoint.x === point.x && point.y !== prevPoint.y) ||
            (point.y === prevPoint.y && nextPoint.y === point.y && point.x !== prevPoint.x)
        );
    });
    return filteredPoints;
};

export const getPathDirection = (source: Point, target: Point) => {
    const verticalDirection = getPathVerticalDirection(source, target);
    if (verticalDirection) {
        return verticalDirection;
    }

    return getPathHorizontalDirection(source, target);
};

const getPathVerticalDirection = (source: Point, target: Point) => {
    if (source.y < target.y) {
        return Direction.DOWN;
    }
    if (source.y > target.y) {
        return Direction.UP;
    }
    return null;
};

const getPathHorizontalDirection = (source: Point, target: Point) => {
    if (source.x < target.x) {
        return Direction.RIGHT;
    }
    if (source.x > target.x) {
        return Direction.LEFT;
    }
    return null;
};
